import { useMemo, type ReactNode } from "react";

import { DataTable, KpiCard } from "../../../../shared";
import { KPI_CARD_BACKGROUND } from "../../../../../utils/Constants";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { convertToUserTimezone } from "../../../../../utils/dateService";
import {
  getKycStatusPresentation,
  type KycStatusTone,
} from "../../../../../utils/kycStatus";
import type {
  ReferredUserSummary,
  UserReferralsKpis,
} from "../userPromotionsTypes";
import styles from "../UserPromotions.module.css";
import { formatCurrency } from "../../../../../utils/financialFormatters";

type ReferralsTabProps = {
  referrals: ReferredUserSummary[];
  kpis: UserReferralsKpis;
  isLoading: boolean;
  dateFilter: ReactNode;
  currencySymbol?: string;
};

const formatDateTime = (value: string | null): string =>
  value
    ? convertToUserTimezone(value, "MM/DD/YYYY hh:mm A")
    : AppStrings.PAM_USER_PROMOTIONS_NOT_APPLICABLE;

const formatAmount = (
  amount: number | null,
  currencySymbol?: string,
): string =>
  amount === null
    ? AppStrings.PAM_USER_PROMOTIONS_NOT_APPLICABLE
    : formatCurrency(amount, currencySymbol, 2);

const KYC_TONE_CLASS: Record<KycStatusTone, string> = {
  success: "verified",
  pending: "pending",
  error: "failed",
  neutral: "inactive",
};

const formatStatus = (status: ReferredUserSummary["status"]): string =>
  status === "active"
    ? AppStrings.PAM_PROMOTIONS_STATUS_ACTIVE
    : AppStrings.INACTIVE;

const createColumns = (currencySymbol?: string) => [
  {
    title: AppStrings.PAM_USER_REFERRALS_IP_ADDRESS,
    dataIndex: "ipAddress",
    key: "ipAddress",
    width: 180,
  },
  {
    title: AppStrings.REFERRAL_CODE,
    dataIndex: "referralCode",
    key: "referralCode",
    width: 150,
  },
  {
    title: AppStrings.ID,
    dataIndex: "id",
    key: "id",
    width: 190,
  },
  {
    title: AppStrings.USERNAME,
    dataIndex: "username",
    key: "username",
    width: 180,
  },
  {
    title: AppStrings.EMAIL,
    dataIndex: "email",
    key: "email",
    width: 240,
  },
  {
    title: AppStrings.PAM_USER_REFERRALS_DEPOSIT,
    dataIndex: "deposit",
    key: "deposit",
    width: 130,
    render: (value: number | null) => formatAmount(value, currencySymbol),
  },
  {
    title: AppStrings.PAM_USER_REFERRALS_DEPOSIT_DATE,
    dataIndex: "depositDate",
    key: "depositDate",
    width: 190,
    render: (value: string | null) => formatDateTime(value),
  },
  {
    title: AppStrings.TOTAL_PURCHASES,
    dataIndex: "totalPurchases",
    key: "totalPurchases",
    width: 160,
    render: (value: number | null) => formatAmount(value, currencySymbol),
  },
  {
    title: AppStrings.TOTAL_BETS,
    dataIndex: "totalBets",
    key: "totalBets",
    width: 130,
  },
  {
    title: AppStrings.TOTAL_PRIZE_REDEMPTIONS,
    dataIndex: "totalPrizeRedemptions",
    key: "totalPrizeRedemptions",
    width: 210,
    render: (value: number | null) => formatAmount(value, currencySymbol),
  },
  {
    title: AppStrings.KYC,
    dataIndex: "kyc",
    key: "kyc",
    width: 130,
    ellipsis: false,
    render: (kyc: ReferredUserSummary["kyc"]) => {
      const presentation = getKycStatusPresentation(kyc);
      return (
        <span
          className={`${styles.status} ${styles[KYC_TONE_CLASS[presentation.tone]]}`}
        >
          {presentation.label}
        </span>
      );
    },
  },
  {
    title: AppStrings.STATUS,
    dataIndex: "status",
    key: "status",
    width: 130,
    ellipsis: false,
    render: (status: ReferredUserSummary["status"]) => (
      <span className={`${styles.status} ${styles[status]}`}>
        {formatStatus(status)}
      </span>
    ),
  },
  {
    title: AppStrings.PAM_USER_REFERRALS_REGISTRATION_SCORE,
    dataIndex: "registrationScore",
    key: "registrationScore",
    width: 180,
  },
  {
    title: AppStrings.PAM_USER_REFERRALS_INVITATION_DATE,
    dataIndex: "invitationDate",
    key: "invitationDate",
    width: 190,
    render: (value: string | null) => formatDateTime(value),
  },
];

function ReferralsTab({
  referrals,
  kpis,
  isLoading,
  dateFilter,
  currencySymbol,
}: ReferralsTabProps) {
  const columns = useMemo(
    () => createColumns(currencySymbol),
    [currencySymbol],
  );

  return (
    <div
      id="user-referrals-panel"
      role="tabpanel"
      aria-labelledby="user-referrals-tab"
      className={styles.tabPanel}
    >
      <div className={styles.summaryRow}>
        <div className={styles.kpis}>
          <KpiCard
            title={AppStrings.PAM_USER_REFERRALS_TOTAL}
            value={kpis.totalReferred}
            maximumFractionDigits={0}
            hidePercentage
            isApiRunning={isLoading}
            currencySymbol={currencySymbol}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
          <KpiCard
            title={AppStrings.PAM_USER_REFERRALS_PURCHASING}
            value={kpis.purchasingUsers}
            maximumFractionDigits={0}
            hidePercentage
            isApiRunning={isLoading}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
          <KpiCard
            title={AppStrings.PAM_USER_REFERRALS_LIFETIME_VALUE}
            value={kpis.lifetimeValue}
            hidePercentage
            isApiRunning={isLoading}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
        </div>
        {dateFilter}
      </div>

      <div className={styles.tableContainer}>
        <DataTable
          id="user-referrals-table"
          dataSource={referrals}
          columns={columns}
          rowKey="id"
          pagination={false}
          loading={isLoading}
          scrollPreset="xUltraWide"
          scrollOverrides={{ x: 2600 }}
          locale={{ emptyText: AppStrings.PAM_USER_REFERRALS_EMPTY }}
          data-testid="user-referrals-table"
        />
      </div>
    </div>
  );
}

export default ReferralsTab;
